import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonWithGAClickButton,
  DEFAULT_IMAGE_TWO_COLUMNS_TRANSFORMATIONS,
  GalleryGrid,
  GalleryModal,
  ImageName,
  Picture,
  Section,
  SectionSubTitle,
  SelectionGroup,
  SelectionGroupOption,
  SelectionGroupValue,
} from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { CEILINGS_ELEMENT_BLOCK } from '../../ceilings-constants';
import { CeilingsTypeTabName } from './ceilings-gallery-section-constants';
import {
  ceilingsGalleryGrid,
  ceilingsGalleryImage,
  ceilingsGalleryImageWrapper,
  GalleryImageWrapper,
  ceilingsGallerySelectionGroup,
  ceilingsGalleryShowMoreBtnWrapper,
} from './ceilings-gallery-section-styles';
import { CeilingsGalleryStructure } from './ceilings-gallery-section-types';
import { getCeilingsGallery, getCeilingsTypeList } from './ceilings-gallery-section-utils';

const INITIAL_IMAGE_PER_PAGE = 7;
const IMAGE_PER_PAGE = 10;

type CeilingsGallerySectionProps = {
  onConsultationSignupBtnClick: () => void;
};

export const CeilingsGallerySection = ({ onConsultationSignupBtnClick }: CeilingsGallerySectionProps) => {
  const { t } = useTranslation();
  const bannerRefs = useRef<(HTMLAnchorElement | HTMLDivElement)[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [lastRefIndex, setLastRefIndex] = useState<number>(0);
  const [activeCeilingTypeTab, setActiveCeilingTypeTab] = useState(CeilingsTypeTabName.MountingType);
  const [imagePerPage, setImagePerPage] = useState(INITIAL_IMAGE_PER_PAGE);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const ceilingsTypeList = useMemo(() => getCeilingsTypeList(), []);
  const galleryList = useMemo(() => getCeilingsGallery(activeCeilingTypeTab), [activeCeilingTypeTab]);

  const galleryImageList = useMemo(() => galleryList.slice(0, imagePerPage), [
    imagePerPage,
    galleryList,
  ]);

  const isShowMoreBtnVisible = useMemo(
    () => imagePerPage < galleryList.length,
    [imagePerPage, galleryList]
  );

  const promotions = useMemo(
    () =>
      galleryImageList.map((image, index: number) => ({
        name: `${CEILINGS_ELEMENT_BLOCK}_bottom`,
        creative: image.name,
        position: (index + 1).toString(),
      })),
    [galleryImageList]
  );

  usePromoViewEvent(bannerRefs, `${CEILINGS_ELEMENT_BLOCK} / gallery`, promotions, lastRefIndex);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setActiveImageIndex(0);
  }, [setIsModalOpen]);

  const handleConsultationSignupBtnClick = useCallback(() => {
    onConsultationSignupBtnClick();
    handleCloseModal();
  }, [onConsultationSignupBtnClick, handleCloseModal]);

  const handleCeilingTypeTabChange = (_: React.MouseEvent, value: SelectionGroupValue) => {
    setActiveCeilingTypeTab(value as CeilingsTypeTabName);
    setImagePerPage(INITIAL_IMAGE_PER_PAGE);

    const activeOption: SelectionGroupOption | undefined = ceilingsTypeList.find(
      (option: SelectionGroupOption) => option.value === value
    );
    if (activeOption) {
      analyticsUtils.clickButtonEvent(
        CEILINGS_ELEMENT_BLOCK,
        activeOption.text,
        'Решение для любого случая'
      );
    }
  };

  const handleShowMoreBtnClick = useCallback(() => {
    setLastRefIndex(imagePerPage);
    setImagePerPage(imagePerPage + IMAGE_PER_PAGE);
    analyticsUtils.clickButtonEvent(CEILINGS_ELEMENT_BLOCK, 'Показать еще', 'Решение для любого случая');
  }, [imagePerPage]);

  const handleImageClick = useCallback(
    (index: number) => () => {
      setIsModalOpen(true);
      setActiveImageIndex(index);
      analyticsUtils.promoClickEvent(`${CEILINGS_ELEMENT_BLOCK} / gallery`, promotions[index]);
    },
    [promotions]
  );

  return (
    <Section>
      <SectionSubTitle margin="m">{t('gallery-section.title')}</SectionSubTitle>
      <SelectionGroup
        active={activeCeilingTypeTab}
        className={ceilingsGallerySelectionGroup}
        onChange={handleCeilingTypeTabChange}
        optionList={ceilingsTypeList}
        rounded
      />

      <GalleryGrid className={ceilingsGalleryGrid}>
        {galleryImageList.map(({ srcSmall, alt, transformations }: CeilingsGalleryStructure, index) => (
          <GalleryImageWrapper
            className={ceilingsGalleryImageWrapper}
            data-position={index}
            key={createElementKey('ceilings-gallery', index)}
            onClick={handleImageClick(index)}
            ref={(el: HTMLDivElement | null) => {
              if (el && bannerRefs) bannerRefs.current[index] = el;
            }}>
            <Picture
              alt={alt}
              className={ceilingsGalleryImage}
              src={srcSmall}
              transformations={transformations || DEFAULT_IMAGE_TWO_COLUMNS_TRANSFORMATIONS}
            />
            <ImageName adaptive={false} name={alt} size="m" />
          </GalleryImageWrapper>
        ))}
      </GalleryGrid>

      {isShowMoreBtnVisible && (
        <div className={ceilingsGalleryShowMoreBtnWrapper}>
          <Button onClick={handleShowMoreBtnClick} rounded>
            {t('gallery-section.showmore-button')}
          </Button>
        </div>
      )}

      <GalleryModal
        clickedImageIndex={activeImageIndex}
        elementBlock={CEILINGS_ELEMENT_BLOCK}
        elementText="Решение для любого случая / modal"
        galleryList={galleryList}
        onClose={handleCloseModal}
        renderFooterButton={() => (
          <ButtonWithGAClickButton
            aria-label={t('design-panel.button') || ''}
            elementBlock={CEILINGS_ELEMENT_BLOCK}
            elementName="Решение для любого случая / modal"
            elementText={t('design-panel.button')}
            invert
            onClick={handleConsultationSignupBtnClick}
            target="_blank"
            title={t('design-panel.button') || ''}
            variant="accent">
            {t('design-panel.button')}
          </ButtonWithGAClickButton>
        )}
        show={isModalOpen}
        isShowImageName
      />
    </Section>
  );
};
